import { Button } from "@d12labs/ui/button";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Toolbar } from "@d12labs/ui/toolbar";
import { cn } from "@d12labs/utils";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CircleCheck, Download } from "lucide-react";
import { useCallback, useMemo, useRef } from "react";
import { harnessLabel } from "../aggregate";
import { downloadText } from "../components/download";
import { GapsTable } from "../components/gaps-table";
import { Segmented, SessionSearch, ToolbarDivider } from "../components/toolbar-controls";
import {
	cleanGapsSearch,
	filterGapRows,
	GAP_CSV_HEADER,
	GAP_KINDS,
	type GapsSearch,
	gapCsvRows,
	gapHarnessOptions,
	gapTotals,
	KIND_LABEL,
	parseGapsSearch,
} from "../gaps-view";
import { useLedgerGaps } from "../hooks";
import { type GapKind, groupGaps, toCsv } from "../overview";

type KindOption = GapKind | "all";

const KIND_OPTIONS: { value: KindOption; label: string }[] = [
	{ value: "all", label: "All" },
	...GAP_KINDS.map((kind) => ({
		value: kind,
		label: KIND_LABEL[kind].charAt(0).toUpperCase() + KIND_LABEL[kind].slice(1),
	})),
];

function Tile({
	label,
	value,
	note,
	warning,
}: {
	label: string;
	value: number;
	note: string;
	warning?: boolean;
}) {
	return (
		<div className="rounded-lg border border-hairline bg-surface-1 p-3">
			<div className="text-eyebrow uppercase text-ink-tertiary">{label}</div>
			<div
				className={cn("display-md mt-1 tabular-nums", warning ? "text-warning" : "text-foreground")}
			>
				{value.toLocaleString("en-US")}
			</div>
			<div className="text-meta text-ink-subtle">{note}</div>
		</div>
	);
}

/** Every file excluded from the ledger's totals, filterable through the URL. */
export function GapsPage() {
	const rawSearch = useSearch({ strict: false }) as Record<string, unknown>;
	const search = useMemo(() => parseGapsSearch(rawSearch), [rawSearch]);
	const navigate = useNavigate();
	const gapsQuery = useLedgerGaps();
	const searchRef = useRef<HTMLInputElement>(null);

	const update = useCallback(
		(patch: Partial<GapsSearch>) =>
			navigate({
				to: "/ledger/gaps",
				search: (prev: GapsSearch) => cleanGapsSearch({ ...prev, ...patch }),
				replace: true,
			}),
		[navigate],
	);
	const setQuery = useCallback((q: string) => update({ q: q || undefined }), [update]);

	const all = useMemo(() => groupGaps(gapsQuery.data ?? []).rows, [gapsQuery.data]);
	const harnesses = useMemo(() => gapHarnessOptions(all), [all]);
	// An unknown harness in the URL falls back to "all" for the control and the rows alike.
	const harness = search.harness && harnesses.includes(search.harness) ? search.harness : undefined;
	const filtered = useMemo(
		() => filterGapRows(all, { ...search, harness }),
		[all, search, harness],
	);
	const totals = gapTotals(filtered);

	const exportCsv = () =>
		downloadText("ledger-gaps.csv", toCsv(GAP_CSV_HEADER, gapCsvRows(filtered)));

	const subtitle = gapsQuery.isLoading
		? "Loading gaps…"
		: all.length
			? `${totals.files.toLocaleString("en-US")} files · ${totals.gaps.toLocaleString("en-US")} gaps`
			: "No gaps";

	return (
		<>
			<PageHeader
				eyebrow="Ledger"
				title="Data gaps"
				subtitle={subtitle}
				actions={
					<Button
						variant="secondary"
						size="sm"
						className="h-8"
						onClick={exportCsv}
						disabled={!filtered.length}
					>
						<Download className="size-3.5" />
						Export CSV
					</Button>
				}
			/>

			{all.length > 0 ? (
				<Toolbar
					className="flex-wrap"
					left={
						<>
							<Segmented
								label="Kind"
								value={search.kind ?? "all"}
								onChange={(kind) => update({ kind: kind === "all" ? undefined : kind })}
								options={KIND_OPTIONS}
							/>
							<ToolbarDivider />
							<Segmented
								label="Harness"
								value={harness ?? "all"}
								onChange={(h) => update({ harness: h === "all" ? undefined : h })}
								options={[
									{ value: "all", label: "All" },
									...harnesses.map((h) => ({ value: h, label: harnessLabel(h) })),
								]}
							/>
						</>
					}
					right={
						<SessionSearch
							value={search.q ?? ""}
							onChange={setQuery}
							inputRef={searchRef}
							placeholder="Search path or model…"
							label="Search path or model"
						/>
					}
				/>
			) : null}

			<PageBody className="space-y-5 px-6 py-5">
				{gapsQuery.isError ? (
					<EmptyState title="Could not load the gaps" body="The gaps request failed." />
				) : null}
				{!gapsQuery.isLoading && !gapsQuery.isError && all.length === 0 ? (
					<EmptyState
						icon={CircleCheck}
						title="No gaps"
						body="Every file in the bucket became a priced session."
					/>
				) : null}
				{all.length > 0 ? (
					<>
						<p className="text-body-sm text-ink-subtle">
							These files are excluded from every total in the ledger.
						</p>
						<div className="grid gap-3 sm:grid-cols-2">
							<Tile
								label="No parser"
								value={totals.noParser}
								note="Cursor transcripts · format unsupported"
							/>
							<Tile
								label="Unpriced turns"
								value={totals.unpricedTurns}
								note="Model missing from the price table"
								warning
							/>
						</div>
						<GapsTable resetKey={[search.kind, harness, search.q].join("|")} rows={filtered} />
					</>
				) : null}
			</PageBody>
		</>
	);
}
